import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BusinessCard from "@/components/BusinessCard";
import { ArticleCard } from "@/components/ArticlesSection";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { TrendingUp, Users, Landmark, Handshake, ArrowRight, Check, Phone, BellRing } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8463987d-01eb-454a-a764-f2d45a87cd41/images/c47fe8fccbf10510273ac1b6082362dfde92360e97da52abb7c861326b0e794a.jpeg";

const benefits = [
  { icon: TrendingUp, title: "Un chiffre d'affaires dès le 1er jour", text: "Clients, fournisseurs et réputation sont déjà là : vous vous rémunérez plus vite qu'en partant de zéro." },
  { icon: Users, title: "Une équipe et un savoir-faire", text: "Des salariés formés, des process rodés et un cédant qui vous accompagne pendant la transition." },
  { icon: Landmark, title: "Un financement plus accessible", text: "Les banques s'appuient sur un historique de résultats : un projet de reprise se finance souvent mieux qu'une création." },
  { icon: Handshake, title: "Un risque mieux maîtrisé", text: "Les comptes, le marché et le potentiel s'analysent avant d'acheter : vous savez où vous mettez les pieds." },
];

const steps = [
  ["Définir votre projet", "Secteur, zone géographique, budget, apport personnel et rôle que vous souhaitez tenir."],
  ["Explorer le catalogue", "Consultez les entreprises à céder, présentées de façon anonyme, et repérez celles qui vous correspondent."],
  ["Signer l'engagement de confidentialité", "Une signature unique vous ouvre l'accès aux dossiers complets de toutes les annonces."],
  ["Analyser et rencontrer le cédant", "Étude des comptes, visite de l'entreprise, échanges avec le dirigeant, avec l'appui de votre conseiller PROAC."],
  ["Financer la reprise", "Apport, prêt bancaire, crédit vendeur, prêts d'honneur : nous vous aidons à construire un plan de financement crédible."],
  ["Négocier, auditer, signer", "Lettre d'intention, audit, protocole et acte de cession avec vos conseils, puis transition aux côtés du cédant."],
];

const fit = [
  "Vous voulez entreprendre sans repartir d'une feuille blanche",
  "Vous avez une expérience de management, commerciale ou technique à valoriser",
  "Vous disposez d'un apport personnel ou pouvez réunir des partenaires",
  "Vous préférez développer une activité existante plutôt que la créer",
];

const faq = [
  ["Faut-il beaucoup d'apport pour reprendre une entreprise ?", "L'apport attendu par les banques représente souvent une part significative du projet, mais il peut être complété par un crédit vendeur, des prêts d'honneur ou des associés. Chaque montage est étudié au cas par cas."],
  ["Les annonces sont-elles confidentielles ?", "Oui. Les entreprises sont présentées de façon anonyme. Le nom et les informations détaillées ne sont accessibles qu'après la signature de l'engagement de confidentialité."],
  ["Combien de temps dure une reprise ?", "Entre la première prise de contact et la signature, il faut généralement compter plusieurs mois : analyse, financement, négociation et audit."],
  ["Le cédant m'accompagne-t-il après la vente ?", "Dans la plupart des cessions, une période de transition est prévue pour vous présenter aux clients, aux fournisseurs et à l'équipe."],
];

const fade = (i = 0) => ({ initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { delay: i * 0.07 } });

const useSeo = () => useEffect(() => {
  const prev = document.title;
  document.title = "Reprendre une entreprise dans le Sud de la France | PROAC Transmission";
  const meta = document.querySelector('meta[name="description"]');
  const prevDesc = meta?.content;
  if (meta) meta.content = "Reprendre une entreprise plutôt que la créer : avantages, étapes, financement et entreprises à reprendre en PACA et Occitanie avec PROAC Transmission.";
  return () => { document.title = prev; if (meta) meta.content = prevDesc; };
}, []);

const Kicker = ({ children }) => <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">{children}</span>;

export default function Reprendre() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [deals, setDeals] = useState([]);
  useSeo();
  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data.filter((a) => a.category === "Reprendre une entreprise"))).catch(() => {});
    api.get("/businesses").then((r) => setDeals((Array.isArray(r.data) ? r.data : r.data.items || []).filter((b) => b.status === "available").slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FDFDFC]" data-testid="reprendre-page">
      <Header />
      <section className="relative min-h-[560px] flex items-center">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1629] via-[#1B2A4A]/85 to-[#1B2A4A]/40" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 w-full py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <Kicker>Repreneurs</Kicker>
            <h1 data-testid="reprendre-title" className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight">Reprendre une entreprise plutôt que la créer</h1>
            <p className="mt-6 text-white/80 text-base sm:text-lg">Entreprendre sur des bases solides : PROAC Transmission vous aide à trouver, évaluer, financer et reprendre l'entreprise qui vous correspond, en PACA et en Occitanie.</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="reprendre-cta-catalogue" onClick={() => navigate("/catalogue")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-7 py-6">Voir les entreprises à reprendre <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <Button data-testid="reprendre-cta-register" onClick={() => navigate("/inscription")} variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1B2A4A] px-7 py-6">Créer mon compte repreneur</Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24" data-testid="reprendre-benefits">
        <div className="max-w-2xl"><Kicker>Pourquoi reprendre ?</Kicker><h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Les atouts de la reprise</h2></div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={b.title} {...fade(i)} className="bg-white border border-gray-200 p-7 hover:border-[#E0C173] transition-colors">
              <div className="w-12 h-12 bg-[#1B2A4A] flex items-center justify-center"><b.icon className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
              <h3 className="mt-5 font-display text-lg text-[#1B2A4A]">{b.title}</h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#1B2A4A] text-white" data-testid="reprendre-steps">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 grid lg:grid-cols-[1fr_1.4fr] gap-14">
          <div><Kicker>Comment ça marche ?</Kicker><h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">Votre reprise en 6 étapes</h2><p className="mt-5 text-white/70">Un conseiller PROAC vous accompagne à chaque étape, de la recherche jusqu'à la signature.</p></div>
          <ol className="space-y-px bg-white/10">
            {steps.map(([t, d], i) => (
              <motion.li key={t} {...fade(i)} className="bg-[#1B2A4A] p-6 flex gap-5">
                <span className="font-display text-3xl text-[#E0C173] w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div><h3 className="font-display text-lg">{t}</h3><p className="mt-1 text-sm text-white/70 leading-relaxed">{d}</p></div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center" data-testid="reprendre-fit">
        <div><Kicker>Est-ce fait pour vous ?</Kicker><h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">La reprise vous correspond si…</h2>
          <p className="mt-5 text-[#475569]">La création reste parfois plus adaptée, par exemple pour un concept totalement nouveau ou un très petit budget. Nos articles vous aident à choisir la bonne stratégie.</p></div>
        <ul className="space-y-4">
          {fit.map((f, i) => <motion.li key={f} {...fade(i)} className="flex gap-4 bg-[#F8F6F0] p-5"><Check className="w-5 h-5 text-[#C9A85A] shrink-0 mt-0.5" /><span className="text-[#1B2A4A]">{f}</span></motion.li>)}
        </ul>
      </section>

      {articles.length > 0 && (
        <section className="bg-[#F8F6F0] border-y border-[#E0C173]/30" data-testid="reprendre-articles">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
            <Kicker>Guides du repreneur</Kicker><h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Bien préparer votre projet</h2>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{articles.map((a, i) => <ArticleCard key={a.slug} a={a} i={i} />)}</div>
          </div>
        </section>
      )}

      {deals.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24" data-testid="reprendre-deals">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div><Kicker>Opportunités</Kicker><h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Entreprises à reprendre</h2></div>
            <Link to="/catalogue" data-testid="reprendre-see-catalogue" className="text-[#1B2A4A] font-medium text-sm flex items-center gap-1 hover:text-[#C9A85A]">Voir tout le catalogue <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">{deals.map((b, i) => <BusinessCard key={b.business_id} b={b} index={i} />)}</div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 sm:px-8 pb-24" data-testid="reprendre-faq">
        <h2 className="font-display text-3xl text-[#1B2A4A] tracking-tight">Questions fréquentes</h2>
        <Accordion type="single" collapsible className="mt-8">
          {faq.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} data-testid={`reprendre-faq-${i}`}>
              <AccordionTrigger className="text-left font-display text-[#1B2A4A]">{q}</AccordionTrigger>
              <AccordionContent className="text-[#475569] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-[#0D1629]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div><h2 className="font-display text-3xl text-white tracking-tight">Prêt à trouver votre entreprise ?</h2><p className="mt-3 text-white/70">Parlez de votre projet avec un conseiller PROAC Transmission.</p></div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="reprendre-final-catalogue" onClick={() => navigate("/catalogue")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-7 py-6">Consulter le catalogue</Button>
            <Button data-testid="reprendre-alert-cta" onClick={() => navigate(user ? "/dashboard#alertes" : "/inscription")} variant="outline" className="rounded-full border-[#E0C173] bg-transparent text-[#E0C173] hover:bg-[#E0C173] hover:text-[#1B2A4A] px-7 py-6"><BellRing className="w-4 h-4 mr-2" /> Créer une alerte</Button>
            <a href={`tel:${PHONE_TEL}`} data-testid="reprendre-phone" className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-7 py-3 hover:border-[#E0C173] transition-colors"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
